import React, { useState, useEffect } from "react";
import {
  Copy,
  Plus,
  Trash2,
  Loader2,
} from "lucide-react";
import { esignTemplatesApi } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect from "../ui/SearchableSelect";
import Toggle from "../ui/Toggle";

const PRIMARY = "var(--color-primary)";
const HOVER = "var(--color-primary-hover)";

const CATEGORY_OPTIONS = [
  { value: "retainer", label: "Retainer" },
  { value: "engagement_letter", label: "Engagement Letter" },
  { value: "nda", label: "NDA" },
  { value: "closing", label: "Closing" },
  { value: "consent", label: "Consent" },
  { value: "authorization", label: "Authorization" },
  { value: "general", label: "General" },
];

const ROLE_OPTIONS = [
  { value: "client", label: "Client" },
  { value: "lawyer", label: "Lawyer" },
  { value: "witness", label: "Witness" },
];

interface DefaultSigner {
  role: string;
  label: string;
  order: number;
}

interface FormState {
  name: string;
  description: string;
  category: string;
  defaults: {
    subject: string;
    message: string;
    sequentialSigning: boolean;
    expirationDays: number;
    remindersEnabled: boolean;
    reminderIntervalDays: number;
  };
  defaultSigners: DefaultSigner[];
}

const INITIAL_FORM: FormState = {
  name: "",
  description: "",
  category: "general",
  defaults: {
    subject: "",
    message: "",
    sequentialSigning: false,
    expirationDays: 30,
    remindersEnabled: true,
    reminderIntervalDays: 3,
  },
  defaultSigners: [{ role: "client", label: "", order: 1 }],
};

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  editTemplate?: any;
}

const CreateEsignTemplateModal: React.FC<Props> = ({
  open,
  onClose,
  onCreated,
  editTemplate,
}) => {
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isEdit = !!editTemplate;

  // Pre-fill form when editing
  useEffect(() => {
    if (editTemplate) {
      setForm({
        name: editTemplate.name || "",
        description: editTemplate.description || "",
        category: editTemplate.category || "general",
        defaults: {
          subject: editTemplate.defaults?.subject || "",
          message: editTemplate.defaults?.message || "",
          sequentialSigning: editTemplate.defaults?.sequentialSigning || false,
          expirationDays: editTemplate.defaults?.expirationDays || 30,
          remindersEnabled:
            editTemplate.defaults?.remindersEnabled !== undefined
              ? editTemplate.defaults.remindersEnabled
              : true,
          reminderIntervalDays:
            editTemplate.defaults?.reminderIntervalDays || 3,
        },
        defaultSigners:
          editTemplate.defaultSigners?.length > 0
            ? editTemplate.defaultSigners.map((s: any) => ({
                role: s.role || "client",
                label: s.label || "",
                order: s.order || 1,
              }))
            : [{ role: "client", label: "", order: 1 }],
      });
    } else {
      setForm(INITIAL_FORM);
    }
    setError("");
  }, [editTemplate, open]);

  const updateField = (field: keyof FormState, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateDefaults = (
    field: keyof FormState["defaults"],
    value: any
  ) => {
    setForm((prev) => ({
      ...prev,
      defaults: { ...prev.defaults, [field]: value },
    }));
  };

  const addSigner = () => {
    setForm((prev) => ({
      ...prev,
      defaultSigners: [
        ...prev.defaultSigners,
        { role: "client", label: "", order: prev.defaultSigners.length + 1 },
      ],
    }));
  };

  const removeSigner = (index: number) => {
    if (form.defaultSigners.length <= 1) return;
    setForm((prev) => ({
      ...prev,
      defaultSigners: prev.defaultSigners.filter((_, i) => i !== index),
    }));
  };

  const updateSigner = (
    index: number,
    field: keyof DefaultSigner,
    value: any
  ) => {
    setForm((prev) => {
      const updated = [...prev.defaultSigners];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, defaultSigners: updated };
    });
  };

  const handleSubmit = async () => {
    // Validate
    if (!form.name.trim()) {
      setError("Template name is required");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        category: form.category,
        documents: [],
        defaultSigners: form.defaultSigners,
        defaults: form.defaults,
      };

      if (isEdit) {
        await esignTemplatesApi.update(editTemplate._id, payload);
      } else {
        await esignTemplatesApi.create(payload);
      }

      onCreated();
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          `Failed to ${isEdit ? "update" : "create"} template`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit Template" : "Create Template"}
      subtitle={
        isEdit
          ? "Update your signing template settings"
          : "Create a reusable signing template"
      }
      icon={Copy}
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50"
            style={{ backgroundColor: PRIMARY }}
            onMouseEnter={(e) => {
              if (!saving)
                e.currentTarget.style.backgroundColor = HOVER;
            }}
            onMouseLeave={(e) => {
              if (!saving)
                e.currentTarget.style.backgroundColor = PRIMARY;
            }}
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? "Save Changes" : "Create Template"}
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Template Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => updateField("name", e.target.value)}
            placeholder="E.g., Standard Retainer Agreement"
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Description
          </label>
          <textarea
            value={form.description}
            onChange={(e) => updateField("description", e.target.value)}
            placeholder="Brief description of this template..."
            rows={2}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors resize-none"
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Category
          </label>
          <SearchableSelect
            options={CATEGORY_OPTIONS}
            value={form.category}
            onChange={(val) => updateField("category", val || "general")}
            placeholder="Select category"
          />
        </div>

        {/* Default Subject */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Default Subject
          </label>
          <input
            type="text"
            value={form.defaults.subject}
            onChange={(e) => updateDefaults("subject", e.target.value)}
            placeholder="Pre-filled subject when using this template"
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
          />
        </div>

        {/* Default Message */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Default Message
          </label>
          <textarea
            value={form.defaults.message}
            onChange={(e) => updateDefaults("message", e.target.value)}
            placeholder="Pre-filled message to signers..."
            rows={3}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors resize-none"
          />
        </div>

        {/* Default Signers */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-slate-700">
              Default Signers
            </label>
            <button
              type="button"
              onClick={addSigner}
              className="flex items-center gap-1 text-xs font-medium transition-colors"
              style={{ color: PRIMARY }}
            >
              <Plus className="h-3.5 w-3.5" />
              Add Signer
            </button>
          </div>
          <div className="space-y-3">
            {form.defaultSigners.map((signer, i) => (
              <div
                key={i}
                className="bg-slate-50 border border-slate-200 rounded-xl p-3"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500">
                    Signer {i + 1}
                  </span>
                  {form.defaultSigners.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSigner(i)}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    value={signer.label}
                    onChange={(e) => updateSigner(i, "label", e.target.value)}
                    placeholder="Label (e.g., Client)"
                    className="col-span-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                  />
                  <SearchableSelect
                    options={ROLE_OPTIONS}
                    value={signer.role}
                    onChange={(val) =>
                      updateSigner(i, "role", val || "client")
                    }
                    placeholder="Role"
                  />
                  <input
                    type="number"
                    value={signer.order}
                    onChange={(e) =>
                      updateSigner(
                        i,
                        "order",
                        parseInt(e.target.value) || 1
                      )
                    }
                    placeholder="Order"
                    min={1}
                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Default Options */}
        <div>
          <label className="text-sm font-medium text-slate-700 mb-3 block">
            Default Options
          </label>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-4">
            {/* Sequential Signing */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700">
                  Sequential Signing
                </p>
                <p className="text-xs text-slate-500">
                  Signers must sign in order
                </p>
              </div>
              <Toggle
                checked={form.defaults.sequentialSigning}
                onChange={(val) => updateDefaults("sequentialSigning", val)}
              />
            </div>

            {/* Expiration Days */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700">
                  Expiration Days
                </p>
                <p className="text-xs text-slate-500">
                  Days until the request expires
                </p>
              </div>
              <input
                type="number"
                value={form.defaults.expirationDays}
                onChange={(e) =>
                  updateDefaults(
                    "expirationDays",
                    parseInt(e.target.value) || 30
                  )
                }
                min={1}
                max={365}
                className="w-20 rounded-xl border border-slate-200 px-3 py-2 text-sm text-center focus:outline-none focus:border-emerald-600 transition-colors"
              />
            </div>

            {/* Reminders Enabled */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700">
                  Reminders Enabled
                </p>
                <p className="text-xs text-slate-500">
                  Send automatic reminders to signers
                </p>
              </div>
              <Toggle
                checked={form.defaults.remindersEnabled}
                onChange={(val) => updateDefaults("remindersEnabled", val)}
              />
            </div>

            {/* Reminder Interval - only shown if reminders enabled */}
            {form.defaults.remindersEnabled && (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    Reminder Interval (days)
                  </p>
                  <p className="text-xs text-slate-500">
                    Days between reminder emails
                  </p>
                </div>
                <input
                  type="number"
                  value={form.defaults.reminderIntervalDays}
                  onChange={(e) =>
                    updateDefaults(
                      "reminderIntervalDays",
                      parseInt(e.target.value) || 3
                    )
                  }
                  min={1}
                  max={30}
                  className="w-20 rounded-xl border border-slate-200 px-3 py-2 text-sm text-center focus:outline-none focus:border-emerald-600 transition-colors"
                />
              </div>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </PremiumModal>
  );
};

export default CreateEsignTemplateModal;
